import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Form, Input, Switch, Checkbox, Skeleton, Select, message } from "antd";
import adminService from "../../../services/AdminService";

/**
 * Returns true when NPI is required: only when adding a new clinician-role user.
 * Exported for unit testing.
 * @param {boolean} isClinicianSelected
 * @param {"add"|"edit"} mode
 */
export function isNpiRequired(isClinicianSelected, mode) {
  return isClinicianSelected && mode === "add";
}

/**
 * Antd Form.Item validator for NPI format.
 * Accepts an empty value (NPI is optional in edit mode); when present, requires
 * exactly 10 digits.
 */
export function validateNpiFormat(_, value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!digits) return Promise.resolve();
  if (digits.length !== 10) return Promise.reject(new Error("Enter valid NPI number"));
  return Promise.resolve();
}

const ClinicianFormModal = ({
  open,
  onCancel,
  onSubmit,
  initialValues,
  roles = [],
  practices = [],
  mode = "add",
}) => {
  const [form] = Form.useForm();

  const [npiLoading, setNpiLoading] = useState(false);

  const debounceRef = useRef(null);
  const requestSeqRef = useRef(0);
  const lastFetchedNpiRef = useRef(null);

  const npiValue = Form.useWatch("npi", form);
  const selectedRoles = Form.useWatch("roles", form);

  const normalizedNpi = useMemo(() => {
    return String(npiValue ?? "").replace(/\D/g, "");
  }, [npiValue]);

  const isClinicianSelected = useMemo(() => {
    return Array.isArray(selectedRoles) && selectedRoles.includes("clinician");
  }, [selectedRoles]);

  const shouldFetchNpi = useMemo(() => {
    if (!open || normalizedNpi.length !== 10) return false;
    
    // Only fetch if NPI is different from the last fetched NPI
    const lastFetched = lastFetchedNpiRef.current 
      ? String(lastFetchedNpiRef.current).replace(/\D/g, "") 
      : null;
    
    return normalizedNpi !== lastFetched;
  }, [open, normalizedNpi]);

  const disableRestFields = npiLoading;

  const LoadingInput = ({ disabled, placeholder }) =>
    npiLoading ? (
      <Skeleton.Input active block style={{ height: 32 }} />
    ) : (
      <Input disabled={disabled} placeholder={placeholder} />
    );

  const LoadingCheckboxGroup = ({ disabled, children }) =>
    npiLoading ? (
      <Skeleton active paragraph={{ rows: 3 }} />
    ) : (
      <Checkbox.Group disabled={disabled} style={{ width: "100%" }}>
        {children}
      </Checkbox.Group>
    );

  const LoadingSwitch = ({ disabled }) =>
    npiLoading ? (
      <Skeleton.Button active style={{ width: 44, height: 22 }} />
    ) : (
      <Switch disabled={disabled} />
    );

  useEffect(() => {
    if (open) {
      const normalized = (() => {
        if (!initialValues) return { is_active: true };
        const { role, roles: multiRoles, ...rest } = initialValues;

        if (role && !multiRoles) {
          return { ...rest, roles: [role] };
        }

        return { ...rest, roles: Array.isArray(multiRoles) ? multiRoles : [] };
      })();

      // Store the initial NPI to prevent fetch on first load in edit mode
      lastFetchedNpiRef.current = mode === "edit" ? (normalized.npi || null) : null;

      form.setFieldsValue({
        is_active: true,
        ...normalized,
      });
    } else {
      // Reset when modal closes
      lastFetchedNpiRef.current = null;
    }
  }, [open, initialValues, form, mode]);

  useEffect(() => {
    if (!open) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!shouldFetchNpi) {
      setNpiLoading(false);
      form.setFields([{ name: "npi", errors: [] }]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const currentSeq = ++requestSeqRef.current;

      setNpiLoading(true);
      form.setFields([{ name: "npi", errors: [] }]);

      try {
        const data = await adminService.getDetailsByNpi(normalizedNpi);

        if (currentSeq !== requestSeqRef.current) return;

        if (!data?.valid) {
          form.setFields([{ name: "npi", errors: ["NPI not found"] }]);
          return;
        }

        const provider = data?.provider ?? {};

        form.setFieldsValue({
          first_name: provider.first_name ?? form.getFieldValue("first_name"),
          last_name: provider.last_name ?? form.getFieldValue("last_name"),
          amd_profile_id: provider.profile_id ?? form.getFieldValue("amd_profile_id"),
          amd_provider_id: provider.provider_id ?? form.getFieldValue("amd_provider_id"),
        });

        // Update the last fetched NPI after successful fetch
        lastFetchedNpiRef.current = normalizedNpi;
      } catch (err) {
        if (currentSeq !== requestSeqRef.current) return;

        form.setFields([
          { name: "npi", errors: ["Unable to fetch NPI details. Please try again."] },
        ]);
        message.error("Failed to fetch NPI details.");
      } finally {
        if (currentSeq === requestSeqRef.current) setNpiLoading(false);
      }
    }, 350);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open, shouldFetchNpi, normalizedNpi, form]);

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        onSubmit(values);
        form.resetFields();
      })
      .catch(() => {});
  };

  return (
    <Modal
      title={mode === "add" ? "Add User" : "Edit User"}
      open={open}
      onOk={handleOk}
      onCancel={() => {
        requestSeqRef.current += 1;
        setNpiLoading(false);

        form.resetFields();
        onCancel();
      }}
      okText={mode === "add" ? "Create" : "Update"}
      maskClosable={false}
      okButtonProps={{ disabled: npiLoading }}
    >
      <Form layout="vertical" form={form} initialValues={{ is_active: true }}>
        
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: "Please enter email" },
            { type: "email", message: "Enter a valid email" },
          ]}
        >
          {LoadingInput({ disabled: disableRestFields, placeholder: "name@example.com" })}
        </Form.Item>

        <Form.Item
          label="Roles"
          name="roles"
          rules={[{ required: true, message: "Please select at least one role" }]}
        >
          {LoadingCheckboxGroup({
            disabled: disableRestFields,
            children: roles.map((r) => (
              <Checkbox key={r} value={r} style={{ display: "flex", marginBottom: 4 }}>
                {r ? r[0].toUpperCase() + r.slice(1).toLowerCase() : r}
              </Checkbox>
            )),
          })}
        </Form.Item>

        <Form.Item label="Practice" name="practice_id">
          <Select
            allowClear
            disabled={disableRestFields}
            placeholder="Assign to a practice (optional)"
            options={practices.map((p) => ({ value: p.id, label: p.name }))}
          />
        </Form.Item>

        <Form.Item
          label="NPI number"
          name="npi"
          rules={[
            { required: isNpiRequired(isClinicianSelected, mode), message: "Please enter NPI number" },
            { validator: validateNpiFormat },
          ]}
        >
          <Input maxLength={10} minLength={10} placeholder="Enter NPI number" inputMode="numeric" />
        </Form.Item>

        <Form.Item label="Name" required>
          <div style={{ display: "flex", gap: 8 }}>
            <Form.Item
              name="first_name"
              rules={[
                { required: true, message: "Please enter first name" },
                { max: 100, message: "First name cannot exceed 100 characters" },
              ]}
              style={{ flex: 1, marginBottom: 0 }}
            >
              {LoadingInput({ disabled: disableRestFields, placeholder: "First name" })}
            </Form.Item>

            <Form.Item
              name="middle_name"
              rules={[{ max: 100, message: "Middle name cannot exceed 100 characters" }]}
              style={{ flex: 1, marginBottom: 0 }}
            >
              {LoadingInput({ disabled: disableRestFields, placeholder: "Middle name" })}
            </Form.Item>

            <Form.Item
              name="last_name"
              rules={[
                { required: true, message: "Please enter last name" },
                { max: 100, message: "Last name cannot exceed 100 characters" },
              ]}
              style={{ flex: 1, marginBottom: 0 }}
            >
              {LoadingInput({ disabled: disableRestFields, placeholder: "Last name" })}
            </Form.Item>
          </div>
        </Form.Item>
        {isClinicianSelected && (
          <>
            <Form.Item
              label="Profile id"
              name="amd_profile_id"
              rules={[
                { required: true, message: "Please enter profile id" },
                { pattern: /^\d+$/, message: "Profile id must be a number" },
              ]}
            >
              {LoadingInput({ disabled: disableRestFields, placeholder: "Enter profile id" })}
            </Form.Item>

            <Form.Item
              label="Provider id"
              name="amd_provider_id"
              rules={[
                { required: true, message: "Please enter provider id" },
                { pattern: /^\d+$/, message: "Provider id must be a number" },
              ]}
            >
              {LoadingInput({ disabled: disableRestFields, placeholder: "Enter provider id" })}
            </Form.Item>
          </>
        )}
        

        <Form.Item label="Is Active" name="is_active" valuePropName="checked">
          {LoadingSwitch({ disabled: disableRestFields })}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ClinicianFormModal;
